/**
 * KEFIT Wishlist
 * Guest wishlist — localStorage only.
 *
 * Storage key:
 *   kefit-wishlist
 *
 * Supported product-card button:
 *   data-kefit-wishlist
 *   data-product-id="..."
 *   data-product-url="..."
 *   data-product-title="..."
 *
 * Wishlist page:
 *   data-kefit-wishlist-page
 *   data-kefit-wishlist-items
 *   data-kefit-wishlist-empty
 *   data-kefit-wishlist-loading
 *   data-kefit-wishlist-page-count
 */

(() => {
  'use strict';

  const STORAGE_KEY = 'kefit-wishlist';

  /* =========================================================
     STORAGE
     ========================================================= */

  const readStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (!raw) return [];

      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) return [];

      /*
       * Current KEFIT wishlist format:
       * ["123", "456"]
       *
       * Also accepts the richer format:
       * [{ id: "123", url: "/products/example" }]
       *
       * This keeps the system backward compatible.
       */

      return parsed
        .map((item) => {
          if (typeof item === 'object' && item !== null) {
            return {
              id: String(item.id || ''),
              url: item.url || ''
            };
          }

          return {
            id: String(item),
            url: ''
          };
        })
        .filter((item) => item.id);
    } catch (error) {
      console.error(
        '[KEFIT Wishlist] Unable to read wishlist.',
        error
      );

      return [];
    }
  };


  const writeStorage = (items) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(items)
      );
    } catch (error) {
      console.error(
        '[KEFIT Wishlist] Unable to save wishlist.',
        error
      );
    }
  };


  const getIds = () => {
    return readStorage().map((item) => item.id);
  };


  const isInWishlist = (productId) => {
    return getIds().includes(String(productId));
  };


  /* =========================================================
     BUTTON STATE
     ========================================================= */

  const updateWishlistButton = (button) => {
    const productId = String(
      button.dataset.productId || ''
    );

    if (!productId) return;

    const active = isInWishlist(productId);

    button.classList.toggle(
      'is-active',
      active
    );

    button.setAttribute(
      'aria-pressed',
      String(active)
    );

    const title =
      button.dataset.productTitle ||
      window.kefitT('wl_product', 'product');

    button.setAttribute(
      'aria-label',
      active
        ? window.kefitT('wl_remove_aria', 'Remove {title} from wishlist', { title: title })
        : window.kefitT('wl_add_aria', 'Add {title} to wishlist', { title: title })
    );
  };


  const updateAllWishlistButtons = () => {
    document
      .querySelectorAll('[data-kefit-wishlist]')
      .forEach(updateWishlistButton);
  };


  /* =========================================================
     COUNT
     ========================================================= */

  const updateWishlistCounts = () => {
    const count = getIds().length;

    document
      .querySelectorAll('[data-kefit-wishlist-count]')
      .forEach((element) => {
        element.textContent = count;

        element.setAttribute(
          'data-count',
          String(count)
        );

        element.hidden = count === 0;
      });

    document
      .querySelectorAll('[data-kefit-wishlist-link]')
      .forEach((element) => {
        element.setAttribute(
          'aria-label',
          count
            ? (count === 1
              ? window.kefitT('wl_aria_one', 'Wishlist, {count} item', { count: count })
              : window.kefitT('wl_aria_other', 'Wishlist, {count} items', { count: count }))
            : window.kefitT('wl_aria', 'Wishlist')
        );
      });

    document
      .querySelectorAll('[data-kefit-wishlist-page-count]')
      .forEach((element) => {
        element.textContent =
          (count === 1
            ? window.kefitT('wl_count_one', '{count} item', { count: count })
            : window.kefitT('wl_count_other', '{count} items', { count: count }));
      });
  };


  const updateEverything = () => {
    updateAllWishlistButtons();
    updateWishlistCounts();
  };


  /* =========================================================
     EVENTS
     ========================================================= */

  const emitChange = (
    productId,
    active
  ) => {
    document.dispatchEvent(
      new CustomEvent(
        'kefit:wishlist:change',
        {
          detail: {
            productId: String(productId),
            active,
            items: getIds()
          }
        }
      )
    );
  };


  /* =========================================================
     ADD
     ========================================================= */

  const add = (
    productId,
    productUrl = ''
  ) => {
    const id = String(productId || '');

    if (!id) return false;

    const items = readStorage();

    const exists = items.some(
      (item) => item.id === id
    );

    if (exists) {
      return false;
    }

    items.push({
      id,
      url: productUrl || ''
    });

    writeStorage(items);

    updateEverything();

    emitChange(id, true);

    return true;
  };


  /* =========================================================
     REMOVE
     ========================================================= */

  const remove = (productId) => {
    const id = String(productId || '');

    if (!id) return false;

    const items = readStorage();

    const newItems = items.filter(
      (item) => item.id !== id
    );

    if (newItems.length === items.length) {
      return false;
    }

    writeStorage(newItems);

    updateEverything();

    emitChange(id, false);

    renderAllWishlistPages();

    return true;
  };


  /* =========================================================
     TOGGLE
     ========================================================= */

  const toggle = (
    productId,
    productUrl = ''
  ) => {
    if (isInWishlist(productId)) {
      return remove(productId);
    }

    return add(
      productId,
      productUrl
    );
  };


  /* =========================================================
     PRODUCT DATA
     ========================================================= */

  const productCache = new Map();


  const getProductUrl = (item) => {
    if (item.url) {
      return item.url.split('?')[0];
    }

    /*
     * If an older wishlist item only has an ID,
     * the page cannot reliably know its handle from
     * localStorage alone.
     *
     * We therefore try product-card source elements
     * already present on the page first.
     */

    const source = document.querySelector(
      `[data-kefit-wishlist][data-product-id="${CSS.escape(item.id)}"]`
    );

    if (source?.dataset.productUrl) {
      return source.dataset.productUrl.split('?')[0];
    }

    return '';
  };


  const fetchProduct = async (item) => {
    const url = getProductUrl(item);

    if (!url) {
      throw new Error(
        `No product URL available for wishlist item ${item.id}.`
      );
    }

    const cacheKey = url;

    if (productCache.has(cacheKey)) {
      return productCache.get(cacheKey);
    }

    const request = fetch(
      `${url}.js`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Product request failed: ${response.status}`
          );
        }

        return response.json();
      })
      .then((product) => {
        product.__kefitUrl = url;

        return product;
      });

    productCache.set(
      cacheKey,
      request
    );

    return request;
  };


  /* =========================================================
     MONEY
     ========================================================= */

  const formatPrice = (cents) => {
    const amount =
      Number(cents || 0) / 100;

    return new Intl.NumberFormat(
      undefined,
      {
        style: 'currency',
        currency: 'AED',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }
    ).format(amount);
  };


  /* =========================================================
     ESCAPE HTML
     ========================================================= */

  const escapeHTML = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };


  /* =========================================================
     ADD TO CART
     ========================================================= */

  const addToCart = async (
    variantId,
    quantity = 1
  ) => {
    if (!variantId) {
      throw new Error(
        window.kefitT('wl_err_variant', 'Missing product variant.')
      );
    }

    const response = await fetch(
      '/cart/add.js',
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: Number(quantity) || 1
            }
          ]
        })
      }
    );

    if (!response.ok) {
      let message =
        window.kefitT('wl_err_add', 'Unable to add this product to cart.');

      try {
        const data =
          await response.json();

        if (data?.description) {
          message =
            data.description;
        }
      } catch (error) {
        /* Keep default message. */
      }

      throw new Error(message);
    }

    const data =
      await response.json();

    document.dispatchEvent(
      new CustomEvent(
        'kefit:cart:added',
        {
          detail: {
            item: data
          }
        }
      )
    );

    /*
     * Tell Shopify/theme cart components
     * that cart content changed.
     */
    document.dispatchEvent(
      new CustomEvent(
        'cart:updated',
        {
          detail: {
            item: data
          }
        }
      )
    );

    return data;
  };


  /* =========================================================
     WISHLIST PAGE CARD
     ========================================================= */

  const createProductCard = (
    item,
    product
  ) => {
    const wrapper =
      document.createElement('article');

    wrapper.className =
      'kefit-wishlist-card';

    wrapper.dataset.productId =
      item.id;

    const variants =
      Array.isArray(product.variants)
        ? product.variants
        : [];

    const availableVariant =
      variants.find(
        (variant) => variant.available
      ) ||
      variants[0];

    const available =
      Boolean(
        availableVariant &&
        availableVariant.available
      );

    const image =
      product.featured_image ||
      product.images?.[0] ||
      '';

    const productUrl =
      product.__kefitUrl ||
      product.url ||
      '#';

    const currentPrice =
      formatPrice(product.price);

    const comparePrice =
      Number(product.compare_at_price || 0);

    const showCompare =
      comparePrice >
      Number(product.price || 0);

    wrapper.innerHTML = `
      <div class="kefit-wishlist-card__image-wrap">

        <a
          class="kefit-wishlist-card__image-link"
          href="${escapeHTML(productUrl)}"
        >
          ${
            image
              ? `
                <img
                  class="kefit-wishlist-card__image"
                  src="${escapeHTML(image)}"
                  alt="${escapeHTML(product.title)}"
                  loading="lazy"
                >
              `
              : `
                <div class="kefit-wishlist-card__image-placeholder">
                  ${window.kefitT('wl_no_image', 'No image')}
                </div>
              `
          }
        </a>

        <button
          type="button"
          class="kefit-wishlist-card__remove"
          data-kefit-wishlist-remove
          data-product-id="${escapeHTML(item.id)}"
          aria-label="${escapeHTML(window.kefitT('wl_remove_aria', 'Remove {title} from wishlist', { title: product.title }))}"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </button>

      </div>

      <div class="kefit-wishlist-card__content">

        ${
          product.vendor
            ? `
              <div class="kefit-wishlist-card__vendor">
                ${escapeHTML(product.vendor)}
              </div>
            `
            : ''
        }

        <a
          class="kefit-wishlist-card__title"
          href="${escapeHTML(productUrl)}"
        >
          ${escapeHTML(product.title)}
        </a>

        <div class="kefit-wishlist-card__price">

          <span class="kefit-wishlist-card__price-current">
            ${escapeHTML(currentPrice)}
          </span>

          ${
            showCompare
              ? `
                <span class="kefit-wishlist-card__price-compare">
                  ${escapeHTML(formatPrice(comparePrice))}
                </span>
              `
              : ''
          }

        </div>

        <div class="kefit-wishlist-card__actions">

          ${
            available
              ? `
                <button
                  type="button"
                  class="kefit-wishlist-card__cart"
                  data-kefit-wishlist-cart
                  data-product-id="${escapeHTML(item.id)}"
                  data-variant-id="${escapeHTML(availableVariant.id)}"
                >
                  ${window.kefitT('wl_add_to_cart', 'Add to cart')}
                </button>
              `
              : `
                <button
                  type="button"
                  class="kefit-wishlist-card__cart"
                  disabled
                >
                  ${window.kefitT('wl_sold_out', 'Sold out')}
                </button>
              `
          }

          <button
            type="button"
            class="kefit-wishlist-card__remove-text"
            data-kefit-wishlist-remove
            data-product-id="${escapeHTML(item.id)}"
          >
            ${window.kefitT('wl_remove', 'Remove')}
          </button>

        </div>

      </div>
    `;

    return wrapper;
  };


  /* =========================================================
     RENDER WISHLIST PAGE
     ========================================================= */

  const renderWishlistPage = async (
    root
  ) => {
    const grid =
      root.querySelector(
        '[data-kefit-wishlist-items]'
      );

    const empty =
      root.querySelector(
        '[data-kefit-wishlist-empty]'
      );

    const loading =
      root.querySelector(
        '[data-kefit-wishlist-loading]'
      );

    if (!grid) return;

    const items =
      readStorage();

    updateWishlistCounts();

    if (!items.length) {
      grid.innerHTML = '';

      if (loading) {
        loading.hidden = true;
      }

      if (empty) {
        empty.hidden = false;
      }

      return;
    }

    if (empty) {
      empty.hidden = true;
    }

    if (loading) {
      loading.hidden = false;
    }

    grid.innerHTML = '';

    /*
     * Keep the exact order in localStorage.
     */
    const results =
      await Promise.allSettled(
        items.map(
          async (item) => ({
            item,
            product:
              await fetchProduct(item)
          })
        )
      );

    if (loading) {
      loading.hidden = true;
    }

    let rendered = 0;

    results.forEach(
      (result) => {
        if (
          result.status !==
          'fulfilled'
        ) {
          /*
           * Important:
           * Do NOT remove the item from
           * localStorage just because the
           * product request failed.
           */
          console.warn(
            '[KEFIT Wishlist] Unable to load item.',
            result.reason
          );

          return;
        }

        const card =
          createProductCard(
            result.value.item,
            result.value.product
          );

        grid.appendChild(card);

        rendered++;
      }
    );

    if (
      rendered === 0 &&
      empty
    ) {
      empty.hidden = false;
    }

    updateWishlistCounts();
  };


  const renderAllWishlistPages = () => {
    document
      .querySelectorAll(
        '[data-kefit-wishlist-page]'
      )
      .forEach((root) => {
        renderWishlistPage(root)
          .catch((error) => {
            console.error(
              '[KEFIT Wishlist]',
              error
            );
          });
      });
  };


  /* =========================================================
     CLICK HANDLING
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {

      /*
       * Wishlist heart
       */
      const wishlistButton =
        event.target.closest(
          '[data-kefit-wishlist]'
        );

      if (wishlistButton) {
        event.preventDefault();
        event.stopPropagation();

        const productId =
          wishlistButton.dataset.productId;

        const productUrl =
          wishlistButton.dataset.productUrl ||
          '';

        if (productId) {
          toggle(
            productId,
            productUrl
          );
        }

        return;
      }


      /*
       * Remove from Wishlist
       */
      const removeButton =
        event.target.closest(
          '[data-kefit-wishlist-remove]'
        );

      if (removeButton) {
        event.preventDefault();

        const productId =
          removeButton.dataset.productId;

        if (productId) {
          remove(productId);
        }

        return;
      }


      /*
       * Add Wishlist item to Cart
       */
      const cartButton =
        event.target.closest(
          '[data-kefit-wishlist-cart]'
        );

      if (cartButton) {
        event.preventDefault();

        if (cartButton.disabled) {
          return;
        }

        const variantId =
          cartButton.dataset.variantId;

        if (!variantId) {
          console.error(
            '[KEFIT Wishlist] Missing variant ID.'
          );

          return;
        }

        const originalHTML =
          cartButton.innerHTML;

        cartButton.disabled = true;

        cartButton.innerHTML =
          '<span>' + window.kefitT('adding', 'Adding...') + '</span>';

        addToCart(variantId)
          .then(() => {

            cartButton.innerHTML =
              '<span>' + window.kefitT('added_to_cart', 'Added to cart') + '</span>';

            setTimeout(() => {
              cartButton.disabled =
                false;

              cartButton.innerHTML =
                originalHTML;
            }, 1400);

          })
          .catch((error) => {

            console.error(
              '[KEFIT Wishlist]',
              error
            );

            cartButton.innerHTML =
              '<span>' + window.kefitT('try_again', 'Try again') + '</span>';

            setTimeout(() => {
              cartButton.disabled =
                false;

              cartButton.innerHTML =
                originalHTML;
            }, 1800);

          });

        return;
      }

    }
  );


  /* =========================================================
     CROSS-PAGE / CROSS-TAB SYNC
     ========================================================= */

  window.addEventListener(
    'storage',
    (event) => {

      if (
        event.key !==
        STORAGE_KEY
      ) {
        return;
      }

      updateEverything();
      renderAllWishlistPages();
    }
  );


  document.addEventListener(
    'kefit:wishlist:change',
    () => {
      updateEverything();
    }
  );


  /* =========================================================
     PUBLIC API
     ========================================================= */

  window.KefitWishlist = {

    key: STORAGE_KEY,

    get: () => getIds(),

    getItems: () => readStorage(),

    has: isInWishlist,

    count: () => getIds().length,

    add,

    remove,

    toggle,

    update: updateEverything,

    refresh: renderAllWishlistPages,

    addToCart

  };


  /* =========================================================
     INITIALIZATION
     ========================================================= */

  const initialize = () => {
    updateEverything();
    renderAllWishlistPages();
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }

})();